import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'
import AssetDownload from '@comp/product/asset-download'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: PageContent = {
    metaTitle: 'Yerli kimlik yönetimi: Türkiye’de geliştirilen IAM ve PAM',
    metaDescription:
      'Monosign ve Monopam, Türkiye kökenli kimlik şirketi Monofor tarafından geliştirilir ve tamamen kendi altyapınızda çalışır; verinin Türkiye’de kalması özel bir talep değil, bir kurulum tercihidir. KVKK raporlaması, Türkçe arayüz, kapalı ağ desteği.',
    heroEyebrow: 'Kullanım senaryoları · Yerli kimlik yönetimi',
    heroTitle: ['Türkiye’de geliştirilen kimlik yönetimi.', 'Kendi altyapınızda.'],
    heroBody:
      'Monosign (IAM + IGA) ve Monopam (PAM), Türkiye kökenli kimlik şirketi Monofor tarafından geliştirilir ve tamamen self-hosted çalışır: kendi altyapınızda veya seçtiğiniz bölgede. Verinin Türkiye’de kalması özel bir talep değil, bir kurulum tercihidir. Bankacılıktan havacılığa ve perakendeye, Türkiye’nin büyük kurumları bu mimari üzerinde çalışıyor.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'Tamamen self-hosted',
      'Bölge seçimi sizde',
      'Kapalı ağ dostu',
    ],
    pillarsEyebrow: 'Ne elde edersiniz',
    pillarsTitle: 'Veri egemenliği vaat değil, mimari.',
    pillarsBody:
      'Ürünü geliştiren ekip yanı başınızdayken ve kurulum kendi altyapınızda yaşarken, veri yerleşimi bir sözleşme maddesi olmaktan çıkar; mimarinin doğal bir sonucu olur.',
    pillars: [
      {
        title: 'Veri Türkiye’de kalır',
        description:
          'Monosign ve Monopam tamamen self-hosted kurulur: şirket içinde veya seçtiğiniz bölgede. Zorunlu bulut bağımlılığı yok; kimlik verisi sınırlarınızın dışına çıkmaz.',
        bullets: [
          'Kendi altyapınızda self-hosted kurulum',
          'Kurulum bölgesi tamamen sizin seçiminiz',
          'Türkiye’de veri yerleşimi için özel düzenleme gerekmez',
          'Kurulum haftalarla değil, dakikalarla ölçülür',
        ],
      },
      {
        title: 'Yerli mühendislik, aynı saat diliminde destek',
        description:
          'Sorularınızı ürünü geliştiren ekipler yanıtlar: aynı saat diliminde yerel mühendislik desteği ve son kullanıcılarınız için Türkçe ürün arayüzü.',
        bullets: [
          'Destek doğrudan ürünü geliştiren mühendislerden',
          'Aynı saat diliminde yanıt; zaman farkı beklemezsiniz',
          'Uçtan uca Türkçe ürün arayüzü',
          'Ürün mühendisliğine doğrudan eskalasyon hattı',
        ],
      },
      {
        title: 'KVKK’ya ve yerel regülasyonlara hazır',
        description:
          'Erişim logları, açık rıza süreçlerini destekleyen raporlama ve denetçiye teslim edebileceğiniz kanıt üretimi ürünün içinde; uyumu göstermek arkeoloji değil, rapor almaktır.',
        bullets: [
          'Her kimlik ve oturum için ayrıntılı erişim logları',
          'Rıza süreçlerini destekleyen, dışa aktarılabilir raporlar',
          'KVKK beklentileriyle eşlenmiş kanıt üretimi',
          'Düzenli aralıklarla erişim gözden geçirme ve sertifikasyon',
        ],
      },
      {
        title: 'Kamu ve kritik altyapı gereksinimleri',
        description:
          'Ayrık ağlar, internete kapalı sahalar ve uzun saklama süreleri desteklenen kurulum biçimleridir, pazarlık edilecek istisnalar değil.',
        bullets: [
          'İzole ağlar için air-gap dostu kurulum',
          'Tamamen ayrık, kapalı ağ bölgelerinde çalışır',
          'Regülasyonların gerektirdiği uzun süreli denetim izi saklama',
          'Güncellemeler dahil uçtan uca şirket içi işletim',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Yerinde kurun. Bağlayın. Kanıtlayın.',
    steps: [
      {
        title: 'Kurun',
        description:
          'Monosign ve Monopam’i kendi altyapınızda (şirket içinde, kapalı ağda veya seçtiğiniz bölgede) dakikalar içinde ayağa kaldırın.',
      },
      {
        title: 'Bağlayın',
        description:
          'Active Directory’yi ve mevcut kaynaklarınızı bağlayın, uygulamaları entegre edin, ayrıcalıklı erişimi kasaya alın.',
      },
      {
        title: 'Kanıtlayın',
        description:
          'Erişim logları, rıza dostu raporlar ve uzun süreli denetim izleri, yerel düzenleyicilerin beklediği kanıtları hazır tutar.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'Bu senaryonun arkasındaki yetenekler.',
    related: [
      {
        label: 'KVKK uyumu',
        description: 'Monofor’un log ve raporlarla KVKK yükümlülüklerine desteği.',
        href: '/compliance/kvkk',
      },
      {
        label: 'Kamu sektörü',
        description: 'Kamu ve kritik altyapı için kimlik yönetimi.',
        href: '/solutions/industries/public-sector',
      },
      {
        label: 'Monopam',
        description: 'Self-hosted ayrıcalıklı erişim yönetimi.',
        href: '/pam',
      },
    ],
}

export default function YerliKimlikYonetimiPage() {
  const c = content
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/tr/solutions/yerli-kimlik-yonetimi"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <AssetDownload
          asset="kvkk-rehber"
          title={{
            en: 'KVKK için kimlik ve erişim rehberi.',
            tr: 'KVKK için kimlik ve erişim rehberi.',
          }}
          copy={{
            en: 'Kişisel veriye erişimi kanıtlanabilir biçimde yönetmek için beş bölümlük kontrol listesi: envanterden değiştirilemez loglara. (Türkçe PDF)',
            tr: 'Kişisel veriye erişimi kanıtlanabilir biçimde yönetmek için beş bölümlük kontrol listesi: erişim envanterinden değiştirilemez loglara.',
          }}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

// TR-only page: the sovereignty story has no international audience, so the
// EN locale 404s and only /tr/solutions/yerli-kimlik-yonetimi exists.
export async function getStaticProps({ locale }: { locale: string }) {
  if (locale !== 'tr') {
    return { notFound: true }
  }
  return { props: {} }
}
